import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useImportDataOptions } from "@/hooks/use-import-data.hook.ts";
import {
  useImportDataParseByType,
  useImportDataPreviewField,
} from "@/queries/import-data.query.ts";
import {
  ApiImportDataType,
  ApiImportFieldPreviewRow,
  ApiImportParseColumn,
} from "@/types/import-data.types.ts";
import {
  Avatar,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface SelectedColumn {
  import_type: ApiImportDataType;
  as_field: string;
}

interface ImportDataFieldPreviewModalProps extends ModalProps {
  parsedColumn: ApiImportParseColumn;
  selectedColumn: SelectedColumn;
  importId: number;
  importType: ApiImportDataType;
}

export function ImportDataFieldPreviewModal({
  importId,
  parsedColumn,
  selectedColumn,
  importType,
  ...props
}: ImportDataFieldPreviewModalProps) {
  const { data: parseOverview, isPending } = useImportDataPreviewField(
    importId,
    parsedColumn.column_index
  );
  const { data: parseData } = useImportDataParseByType(importId, importType);
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { getImportDataFieldTitle, getImportDataTypeOptions } = useImportDataOptions();

  const caption = useMemo(() => {
    const importTypeTitle =
      getImportDataTypeOptions().find((o) => o.id === selectedColumn.import_type)?.title ?? "";

    let fieldTitle = selectedColumn.as_field;
    if (parseData) {
      let possibleFields;
      switch (selectedColumn.import_type) {
        case ApiImportDataType.custom_table:
          possibleFields = parseData.possible_tables?.flatMap((t) => t.fields);
          break;
        default:
          possibleFields = parseData.possible_fields;
          break;
      }
      const entity = possibleFields?.find((f) => f.field_key === selectedColumn.as_field);
      if (entity) {
        fieldTitle = getImportDataFieldTitle(selectedColumn.import_type, entity);
      }
    }

    return `${parsedColumn.header} → ${importTypeTitle}: ${fieldTitle}`;
  }, [parsedColumn.header, selectedColumn, parseData]);

  const tableColumns = useMemo<ColumnDef<ApiImportFieldPreviewRow>[]>(
    () => [
      {
        id: "employee",
        header: t("common:employee"),
        cell: (cell) => {
          const row = cell.row.original;
          if (row.employee) {
            return (
              <Link to={`/employees/${row.employee.id}/`} target={"_blank"}>
                <Stack direction={"horizontal"} gap={"sm"} items={"center"} className={"pr-4"}>
                  <EmployeeAvatar employee={row.employee} size={"sm"} />
                  <Text className={"text-nowrap text-ellipsis text-dark"}>
                    {renderEmployeeName(row.employee)}
                  </Text>
                </Stack>
              </Link>
            );
          }
          return <Text className="text-secondary">{row.employee_identifier ?? "—"}</Text>;
        },
      },
      {
        id: "old_value",
        header: t("import_data:current_value"),
        cell: (cell) => {
          const row = cell.row.original;
          return <Text className="text-secondary">{row.old_value ?? "—"}</Text>;
        },
      },
      {
        id: "new_value",
        header: t("import_data:new_value"),
        cell: (cell) => {
          const row = cell.row.original;
          const isNewValue =
            row.new_value && parseOverview?.values_to_create?.includes(row.new_value);
          return (
            <Stack direction="horizontal" gap="sm" items="center">
              {isNewValue && (
                <Tooltip>
                  <TooltipTrigger>
                    <Avatar emoji={":writing_hand:"} size={"sm"} />
                  </TooltipTrigger>
                  <TooltipContent>{t("import_data:new_value_in_directory")}</TooltipContent>
                </Tooltip>
              )}
              <Text className="text-title">{row.new_value ?? "—"}</Text>
            </Stack>
          );
        },
      },
    ],
    [parseOverview]
  );

  return (
    <Modal
      title={t("import_data:preview_import")}
      caption={caption}
      {...props}
      size={"lg"}
      withCloser
    >
      <Stack gap="lg">
        {isPending ? (
          <Stack gap="md">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-40 w-full" />
          </Stack>
        ) : (
          <>
            <ListItem
              layout={"two-columns"}
              behavior="card"
              titleSlot={
                <ListItem
                  behavior="card"
                  title={t("import_data:updated_records")}
                  caption={`${parseOverview?.will_update ?? 0}`}
                />
              }
              valueSlot={
                <ListItem
                  behavior="card"
                  title={t("import_data:new_records")}
                  caption={`${parseOverview?.will_create ?? 0}`}
                />
              }
            />
            {parseOverview?.diff && parseOverview.diff.length > 0 && (
              <Table data={parseOverview.diff} columns={tableColumns} />
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}
